/**
 * Firestore för Kundprojekt: läsning, sparande och radering i en transaktion
 * som samtidigt håller de avledda fälten i synk:
 *   crmCompanies/{kundId}.kundprojektIds
 *   crmCompanies/{kundId}/contacts/{kontaktId}.kundprojektIds
 *   crmLeads/{leadId}.kundprojektIds
 * och sätter kundflaggorna (erfarenhetSakerhetsskydd, harIsoleradMiljo).
 * Vad som ändras räknas ut i kundprojekt.js.
 *
 * Kopplade dokument som inte finns längre (raderad kontakt eller lead) hoppas
 * över, så att de inte stoppar sparandet.
 */
import {
  collection, doc, getDocs, getDoc, query, where, documentId, runTransaction, updateDoc, onSnapshot,
  arrayUnion, arrayRemove, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { normaliseraKundprojekt, valideraKundprojekt, synkAndringar, kundflaggor, slaIhop } from './kundprojekt';
import { toMillis } from './pipelineFlode';

const KP = 'kundprojekt';

const franDoc = d => ({ id: d.id, ...d.data() });

export async function laddaKundprojekt(id) {
  const d = await getDoc(doc(db, KP, id));
  return d.exists() ? franDoc(d) : null;
}

export async function laddaAllaKundprojekt() {
  return (await getDocs(collection(db, KP))).docs.map(franDoc);
}

/** Kundprojekt med givna id:n, t.ex. kundens, kontaktens eller leadets kundprojektIds. */
export async function laddaKundprojektMedIds(ids) {
  const unika = [...new Set((ids || []).filter(Boolean))];
  const svar = [];
  for (let i = 0; i < unika.length; i += 30) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDocs(query(collection(db, KP), where(documentId(), 'in', unika.slice(i, i + 30))));
    svar.push(...snap.docs.map(franDoc));
  }
  return svar;
}

function kontaktRef(nyckel) {
  const [kundId, kontaktId] = nyckel.split('/');
  return doc(db, 'crmCompanies', kundId, 'contacts', kontaktId);
}

/**
 * Läser de kopplade dokumenten (transaktioner kräver läsning före skrivning)
 * och skriver de avledda fälten och kundflaggorna på dem som finns.
 */
async function synka(tx, kpId, fore, efter) {
  const a = synkAndringar(fore, efter);
  const flaggor = kundflaggor(fore, efter);

  const skrivningar = new Map(); // sökväg → { ref, falt }
  const lagg = (ref, falt) => {
    const s = skrivningar.get(ref.path) || { ref, falt: {} };
    Object.assign(s.falt, falt);
    skrivningar.set(ref.path, s);
  };
  for (const id of a.kunder.lagg) lagg(doc(db, 'crmCompanies', id), { kundprojektIds: arrayUnion(kpId) });
  for (const id of a.kunder.ta) lagg(doc(db, 'crmCompanies', id), { kundprojektIds: arrayRemove(kpId) });
  for (const [id, falt] of Object.entries(flaggor)) lagg(doc(db, 'crmCompanies', id), falt);
  for (const n of a.kontakter.lagg) lagg(kontaktRef(n), { kundprojektIds: arrayUnion(kpId) });
  for (const n of a.kontakter.ta) lagg(kontaktRef(n), { kundprojektIds: arrayRemove(kpId) });
  for (const id of a.leads.lagg) lagg(doc(db, 'crmLeads', id), { kundprojektIds: arrayUnion(kpId) });
  for (const id of a.leads.ta) lagg(doc(db, 'crmLeads', id), { kundprojektIds: arrayRemove(kpId) });

  const lista = [...skrivningar.values()];
  const finns = await Promise.all(lista.map(s => tx.get(s.ref).then(d => d.exists())));
  const saknas = [];
  lista.forEach((s, i) => (finns[i] ? s : saknas.push(s.ref.path)));
  return {
    andringar: a, flaggor, saknas,
    skriv: () => lista.forEach((s, i) => { if (finns[i]) tx.update(s.ref, s.falt); }),
  };
}

/**
 * Skapar (id saknas) eller uppdaterar ett kundprojekt och synkar kopplingarna.
 * Kastar Error med valideringsfelen om något saknas.
 * @returns {{ id, kundprojekt, andringar, flaggor, saknas }}
 */
export async function sparaKundprojekt(kp, uid) {
  const efter = normaliseraKundprojekt(kp);
  const fel = valideraKundprojekt(efter);
  if (fel.length) throw new Error(fel.join(' '));

  const ref = kp.id ? doc(db, KP, kp.id) : doc(collection(db, KP));
  const { id: _id, skapad: _s, skapadAv: _sa, uppdaterad: _u, ...data } = efter;

  return runTransaction(db, async tx => {
    const snap = kp.id ? await tx.get(ref) : null;
    const fore = snap?.exists() ? snap.data() : null;
    if (kp.id && !fore) throw new Error('Kundprojektet finns inte längre.');
    const synk = await synka(tx, ref.id, fore, efter);
    tx.set(ref, {
      ...data,
      uppdaterad: serverTimestamp(),
      ...(fore ? {} : { skapad: serverTimestamp(), skapadAv: uid || '' }),
    }, { merge: true });
    synk.skriv();
    const { skriv: _w, ...rest } = synk;
    return { id: ref.id, kundprojekt: { ...efter, id: ref.id }, ...rest };
  });
}

/**
 * Sparar ändringar i ett befintligt kundprojekt: bara fälten som skiljer
 * mellan `oppnad` (versionen användaren utgick från) och `redigerad` skrivs,
 * ovanpå den aktuella versionen i databasen. Senaste ändring per fält vinner.
 * Avledda fält och kundflaggor synkas i samma transaktion.
 *
 * @returns {{ id, kundprojekt, andringar, flaggor, saknas, egna, konflikt }}
 *   egna: fälten som skrevs. konflikt: dokumentet hade ändrats av någon
 *   annan sedan det öppnades (uppdaterad skiljer).
 */
export async function sparaKundprojektAndringar(oppnad, redigerad, uid) {
  const id = oppnad.id;
  const ref = doc(db, KP, id);
  const resultat = await runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Kundprojektet finns inte längre. Det kan ha raderats i kundprojektvyn.');
    const aktuell = snap.data();
    const { kundprojekt: efter, egna } = slaIhop(aktuell, oppnad, redigerad);
    const fel = valideraKundprojekt(efter);
    if (fel.length) throw new Error(fel.join(' '));
    const konflikt = (toMillis(aktuell.uppdaterad) ?? 0) !== (toMillis(oppnad.uppdaterad) ?? 0);
    const synk = await synka(tx, id, aktuell, efter);
    if (egna.length) {
      // Normaliseringen kan röra fler fält än användaren (t.ex. säkerhetsfält
      // som nollställs vid nej) – skriv allt som skiljer från databasen.
      const skriv = {};
      for (const [k, v] of Object.entries(efter)) {
        if (['id', 'skapad', 'skapadAv', 'uppdaterad', 'uppdateradAv'].includes(k)) continue;
        if (JSON.stringify(v ?? null) !== JSON.stringify(aktuell[k] ?? null)) skriv[k] = v;
      }
      tx.update(ref, { ...skriv, uppdaterad: serverTimestamp(), uppdateradAv: uid || '' });
      synk.skriv();
    }
    const { skriv: _w, ...rest } = synk;
    return { id, kundprojekt: { ...aktuell, ...efter, id }, ...rest, egna, konflikt };
  });
  // Läs om, så att uppdaterad är serverns – annars ser nästa sparande ut som en konflikt.
  const farsk = await laddaKundprojekt(id).catch(() => null);
  return farsk ? { ...resultat, kundprojekt: farsk } : resultat;
}

/**
 * Lyssnar på ett kundprojekt medan det redigeras. cb(kundprojekt | null).
 * Returnerar avregistreringsfunktionen.
 */
export function bevakaKundprojekt(id, cb) {
  return onSnapshot(doc(db, KP, id), d => cb(d.exists() ? franDoc(d) : null), () => {});
}

/** Raderar kundprojektet och tar bort det från kunder, kontakter och leads. */
export async function raderaKundprojekt(id) {
  const ref = doc(db, KP, id);
  return runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return null;
    const synk = await synka(tx, id, snap.data(), null);
    synk.skriv();
    tx.delete(ref);
    const { skriv: _w, ...rest } = synk;
    return rest;
  });
}

/**
 * Fyller i aktivitets-id när aktiviteten som kundprojektet registrerades vid
 * har sparats (kundprojektet skapas innan aktiviteten finns).
 */
export async function kopplaRegistreringsaktivitet(kundprojektId, leadId, aktivitetId) {
  await updateDoc(doc(db, KP, kundprojektId), {
    registreradVid: { leadId: leadId || '', aktivitetId: aktivitetId || '' },
    uppdaterad: serverTimestamp(),
  });
}

/** Kundens säkerhetsflaggor och anteckning, satta manuellt. */
export async function sparaKundSakerhet(kundId, { erfarenhetSakerhetsskydd, harIsoleradMiljo, sakerhetsAnteckning }) {
  const falt = {};
  if (erfarenhetSakerhetsskydd !== undefined) falt.erfarenhetSakerhetsskydd = erfarenhetSakerhetsskydd;
  if (harIsoleradMiljo !== undefined) falt.harIsoleradMiljo = harIsoleradMiljo;
  if (sakerhetsAnteckning !== undefined) falt.sakerhetsAnteckning = String(sakerhetsAnteckning || '').trim();
  await updateDoc(doc(db, 'crmCompanies', kundId), { ...falt, updatedAt: new Date() });
  return falt;
}

/** Leads med givna id:n (för kundprojektens leadkopplingar på kundkortet). */
export async function laddaLeadsMedIds(ids) {
  const unika = [...new Set((ids || []).filter(Boolean))];
  const svar = [];
  for (let i = 0; i < unika.length; i += 30) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDocs(query(collection(db, 'crmLeads'), where(documentId(), 'in', unika.slice(i, i + 30))));
    svar.push(...snap.docs.map(franDoc));
  }
  return svar;
}
